// client/src/utils/marksSheet.generated.js
//
// GENERATED - do not edit by hand.
//
// Copied from packages/core/src/portal/teacher.js by tools/sync-shared.py. Edit the core file and re-run that
// script; `node tools/marks-sheet-conformance.mjs` asserts this copy has not
// drifted from it.
//
// The phone imports the original. The web cannot (packages/core is outside
// client/src, and the CRA build will not resolve it), so it gets this. They must
// stay identical - a rule that exists twice and differs is the whole class of
// bug these files were written to end.
//
// Who is in a marks sheet. The web fell back to the class list when the
// elective roster came back empty and the phone did not, so the same
// teacher saw a full sheet on one client and an empty screen on the other.

import { fetchJson } from './fetchJson';

const enc = encodeURIComponent;

/**
 * DOES THIS CLASS PICK ITS SUBJECTS, or does everyone in it take everything?
 *
 * Second-cycle and examination classes have electives, so a subject's roster is
 * a genuine subset of the class and an empty one means "nobody has chosen it" —
 * a real answer that must be shown as such. Everywhere else every student in the
 * class takes the subject, so an empty elective roster means the electives table
 * was simply never populated, and the class list is the right answer.
 *
 * Both clients were making this decision; only one of them was making it.
 */
export function selectionRequiredForClass(classRow) {
  const level = String(classRow?.level || '');
  const isSecondary = level === 'first_cycle' || level === 'second_cycle';
  return isSecondary && (level === 'second_cycle' || !!classRow?.isExaminationClass);
}

/**
 * THE MARKS SHEET: who is in it, and what is already written down.
 *
 * This existed twice and the two copies did not agree, which is what produced
 * "the web shows students and the phone shows none" for the same teacher, class
 * and subject. Two things differed, and each was enough on its own:
 *
 *   THE FALLBACK. The elective roster (StudentSubject) is empty for any class
 *   that does not run electives — which is most of them. The web fell back to
 *   the class list; the phone took the empty array as the answer and drew an
 *   empty screen. THIS is the one that made ICT show no students on the phone.
 *
 *   THE EXISTING MARKS. The web loaded them and pre-filled the fields; the phone
 *   never asked. A teacher correcting one mark on a phone saw every other field
 *   blank, and had no way to tell an unrecorded mark from one already filed.
 *
 * It lives here so there is one answer to "who is in this sheet" for both
 * clients. `headers` is an override only — fetchJson already attaches the school,
 * phase and academic-year headers from whatever the platform has stored, so
 * neither caller has to know where the other keeps its scope.
 *
 * @returns {Promise<{students: Array, marksById: Object, values: Object}>}
 *   `values` is keyed by student id and holds the existing score (fixed to one
 *   decimal) or rubric rating as a STRING, ready to seed a text field.
 */
export async function loadMarksSheet({
  classId,
  subjectId,
  evaluationId,
  sequenceNo = '',
  nursery = false,
  selectionRequired = false,
  headers = {},
} = {}) {
  const empty = { students: [], marksById: {}, values: {} };
  if (!classId || !subjectId) return empty;

  // 1. The elective roster first, because where it is populated it is the
  //    precise answer — the students who actually chose this subject.
  const roster = await fetchJson(
    `/api/allocations/roster/${enc(classId)}/${enc(subjectId)}`,
    { headers },
  ).catch(() => null);
  let students = Array.isArray(roster?.students) ? roster.students : [];

  // 2. The class list, but ONLY where an empty elective roster cannot be the
  //    real answer. In a class that picks its subjects, "nobody chose this" has to
  //    stay visible rather than being papered over with the whole class.
  if (!selectionRequired && students.length === 0) {
    const all = await fetchJson(
      `/api/allocations/students/class/${enc(classId)}`,
      { headers, fresh: REFERENCE_FRESH_MS },
    ).catch(() => null);
    students = Array.isArray(all?.students) ? all.students : [];
  }

  // 3. What is already recorded. Without an evaluation there is nothing to key
  //    marks by, so the sheet is legitimately blank — but the roster above is
  //    still worth returning, because the phone shows the names before an
  //    evaluation is picked.
  if (!evaluationId) return { ...empty, students };

  const params = new URLSearchParams({
    classId: String(classId),
    subjectId: String(subjectId),
    evaluationId: String(evaluationId),
    ...(sequenceNo ? { sequenceNo: String(sequenceNo) } : {}),
  });
  const raw = await fetchJson(
    `${nursery ? '/api/nursery-marks' : '/api/marks'}?${params.toString()}`,
    { headers },
  ).catch(() => []);

  const marksById = {};
  for (const m of Array.isArray(raw) ? raw : []) {
    // The student id arrives by three different routes depending on how the row
    // was joined. Taking only the first would silently drop the other two.
    const sid = m?.Enrollment?.studentId ?? m?.Enrollment?.Student?.id ?? m?.studentId;
    if (!sid) continue;
    const value = nursery ? (m?.rating ?? null) : ((m?.score ?? m?.mark) ?? null);
    marksById[String(sid)] = nursery ? { ...m, rating: value } : { ...m, score: value };
  }

  const values = {};
  for (const s of students) {
    const existing = nursery ? marksById[String(s.id)]?.rating : marksById[String(s.id)]?.score;
    const n = Number(existing);
    values[s.id] = Number.isFinite(n)
      ? (nursery ? String(Math.trunc(n)) : n.toFixed(1))
      : '';
  }

  return { students, marksById, values };
}

/**
 * Whether a teacher's own subjects are worth listing to them.
 *
 * NOT for a nursery or primary CLASS TEACHER. They take every subject their
 * class is allocated, so the list is the class's syllabus rather than anything
 * personal — it says nothing they do not already know and crowds out the things
 * that differ day to day. Everyone else has a subset worth stating: a secondary
 * teacher takes a few of many, and a nursery or primary teacher who is NOT a
 * class teacher is a specialist — music, sport, languages — whose two or three
 * subjects are exactly what distinguishes their week.
 *
 * @param {{phaseCode?:string, isClassTeacher?:boolean}} who
 */
export function showsOwnSubjects({ phaseCode, isClassTeacher } = {}) {
  const nurPri = String(phaseCode || '').trim().toLowerCase() === 'nur_pri';
  return !(nurPri && isClassTeacher);
}

/** "first_cycle" -> "First Cycle". Empty for anything unrecognised. */
export function subjectLevelLabel(level) {
  const l = String(level || '').trim().toLowerCase();
  if (l === 'first_cycle') return 'First Cycle';
  if (l === 'second_cycle') return 'Second Cycle';
  if (l === 'nursery') return 'Nursery';
  if (l === 'primary') return 'Primary';
  return '';
}

/**
 * WHAT THE "MY ASSIGNMENTS" CARD SHOWS, decided once for both dashboards.
 *
 * The two phases describe a teacher differently and the card has to follow,
 * because the words are the school's own:
 *
 *   NUR_PRI CLASS TEACHER — one line, "Class teacher of <class>". They take
 *   every subject their class is allocated, so a subject list is the class's
 *   syllabus rather than anything personal, and "class master" is not a word
 *   this phase uses. One responsibility, one line.
 *
 *   NUR_PRI SUBJECT TEACHER — a specialist: music, sport, a language. Their
 *   subjects are exactly what distinguishes their week, and the classes they
 *   visit tell them where they are expected. Both, and no class-teacher line —
 *   they are one or the other, never both.
 *
 *   SECONDARY — unchanged. A class master runs a class WITHOUT teaching all of
 *   it, so they legitimately have their own subjects as well, and heads of
 *   department exist only here.
 *
 * Returned as flags rather than rendered here because the two clients draw
 * chips very differently; what they must not do is disagree about WHICH rows
 * belong to whom, which is what happened when each decided for itself.
 *
 * @param {{phaseCode?:string, isClassTeacher?:boolean}} who
 * @returns {{classesLabel:'classTeacherOf'|'classesTaught', showSubjects:boolean,
 *            showClassMasterOf:boolean, showHeadOfDepartment:boolean}}
 */
export function assignmentSections({ phaseCode, isClassTeacher } = {}) {
  const nurPri = String(phaseCode || '').trim().toLowerCase() === 'nur_pri';
  const nurPriClassTeacher = nurPri && Boolean(isClassTeacher);

  return {
    // The class row is RELABELLED rather than duplicated: a nur_pri class
    // teacher's class list is their one class, so a separate "class master of"
    // row underneath said the same thing twice in the wrong vocabulary.
    classesLabel: nurPriClassTeacher ? 'classTeacherOf' : 'classesTaught',
    showSubjects: showsOwnSubjects({ phaseCode, isClassTeacher }),
    // "Class master" is a secondary word. In nursery/primary the same column
    // means CLASS TEACHER, and that is already said by the row above.
    showClassMasterOf: !nurPri,
    // No departments in nursery/primary, so no head of department.
    showHeadOfDepartment: !nurPri,
  };
}

// HOW OLD AN ANSWER THESE SCREENS WILL SHOW INSTANTLY.
//
// Reference data: the classes a school has, the subjects it teaches, who is in
// a class. It changes when somebody admits a pupil or creates a class, which is
// a handful of times a term — and every screen in the product fetches it again
// on arrival, so the wait to move between two screens was mostly the app asking
// a server on another continent to confirm a list it already had.
//
// fetchJson serves a cached copy younger than this at once and refreshes behind
// it, and any write to the same resource forgets it — so an admission shows up
// on the next read, not a minute later.
//
// Deliberately not applied to marks, balances, attendance or anything a
// decision is made on. Those ask the server and wait, as they always did.
export const REFERENCE_FRESH_MS = 120_000;
